<template>
  <div class="all-notifications">
    <header class="all-header">
      <h2>Todas as Notificações</h2>
      <div class="controls">
        <label class="filter">
          <input type="checkbox" v-model="onlyUnreadFlag" />
          Apenas não lidas
        </label>
        <button class="mark" type="button" @click="markAllAsRead">Marcar todas como lidas</button>
        <!-- Close button might be redundant if inside a modal, but keeping it as per plan -->
        <button class="close" type="button" @click="$emit('close')">Fechar</button>
      </div>
    </header>

    <section class="all-list">
      <div v-if="isLoadingInitial" class="empty">Carregando...</div>
      <div v-else-if="!visibleNotifications.length" class="empty">Nenhuma notificação</div>

      <article v-for="n in visibleNotifications" :key="n.id" class="row" :class="{ 'row--unread': !n.isRead }">
        <div class="row-main">
          <h3 class="row-title">{{ n.title }}</h3>
          <p class="row-message">{{ n.message }}</p>
          <small class="row-meta">
            <span v-if="n.module">{{ n.module }} • </span>
            {{ new Date(n.createdAt).toLocaleString() }}
          </small>
        </div>
        <div class="row-actions">
          <button class="row-btn" type="button" @click="markAsRead(n.id)" :disabled="n.isRead">
            {{ n.isRead ? 'Lida' : 'Marcar como lida' }}
          </button>
        </div>
      </article>

      <div class="more" v-if="hasMore">
        <button class="more-btn" type="button" :disabled="isLoadingMore" @click="loadMore">
          {{ isLoadingMore ? 'Carregando...' : 'Carregar mais' }}
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import { computed } from '@vue/composition-api'
import { useSignalR } from '@/plugins/signalr'

export default {
  emits: ['close'],
  setup(props, { emit }) {
    const api = useSignalR()

    const onlyUnreadFlag = computed({
      get: () => api.onlyUnread.value,
      set: (v) => api.setOnlyUnread(!!v),
    })

    const {
      visibleNotifications,
      isLoadingInitial,
      isLoadingMore,
      hasMore,
      loadMore,
      markAsRead,
      markAllAsRead,
    } = api

    return {
      onlyUnreadFlag,
      visibleNotifications,
      isLoadingInitial,
      isLoadingMore,
      hasMore,
      loadMore,
      markAsRead,
      markAllAsRead,
    }
  }
}
</script>

<style scoped>
:root {
  --notif-bg: #ffffff;
  --notif-text: #111827;
  --notif-muted: #6b7280;
  --notif-border: rgba(0, 0, 0, 0.08);
  --notif-border-strong: rgba(0, 0, 0, 0.12);
  --notif-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  --notif-accent: #ef4444;
  --notif-surface: #f9fafb;
}

.all-notifications {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 80vh;
  max-height: 800px;
  background: var(--notif-bg, #fff);
  overflow: hidden;
  color: var(--notif-text, #111827);
}

.all-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--notif-border, #e5e7eb);
  background: #fff;
}

.all-header h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--notif-text, #111827);
}

.controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--notif-muted, #6b7280);
  font-size: 0.875rem;
  cursor: pointer;
  user-select: none;
}

.mark,
.close {
  border: 1px solid var(--notif-border, #e5e7eb);
  background: #fff;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--notif-text, #374151);
  transition: all 0.2s;
}

.mark:hover,
.close:hover {
  background: var(--notif-surface, #f9fafb);
  border-color: var(--notif-border-strong, #d1d5db);
}

.all-list {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f9fafb;
}

.empty {
  padding: 40px;
  color: var(--notif-muted, #6b7280);
  text-align: center;
  font-size: 1rem;
}

.row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--notif-border, #e5e7eb);
  border-radius: 8px;
  background: #fff;
  transition: all 0.2s;
}

.row:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.row--unread {
  background: #fff;
  border-left: 4px solid var(--notif-accent, #ef4444);
}

.row-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.row-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--notif-text, #111827);
}

.row-message {
  margin: 4px 0;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.5;
}

.row-meta {
  color: var(--notif-muted, #9ca3af);
  font-size: 0.8rem;
  margin-top: 4px;
}

.row-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.row-btn {
  border: 1px solid var(--notif-border, #e5e7eb);
  background: #fff;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--notif-muted, #6b7280);
  transition: all 0.2s;
  white-space: nowrap;
}

.row-btn:hover:not(:disabled) {
  background: var(--notif-surface, #f3f4f6);
  color: var(--notif-text, #374151);
}

.row-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f3f4f6;
}

.more {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.more-btn {
  border: 1px solid var(--notif-border, #e5e7eb);
  background: #fff;
  padding: 10px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  color: var(--notif-text, #374151);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.more-btn:hover:not(:disabled) {
  background: var(--notif-surface, #f9fafb);
}

@media (max-width: 600px) {
  .all-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
  }

  .controls {
    flex-wrap: wrap;
    width: 100%;
    gap: 8px;
  }

  .row {
    flex-direction: column;
  }

  .row-actions {
    flex-direction: row;
    justify-content: flex-end;
  }
}
</style>
